import { getCustomerPortalUrl } from '@/services/PaymentService';
import type { GameData } from '@/interfaces';

interface CustomerPortalOptions {
  gameData?: GameData;
  target?: string;
  onLoadingStart?: () => void;
}

/**
 * Opens the customer portal URL, with fallback to game data URLs if the API call fails
 * @param options - Configuration options including gameData, target, and loading callbacks
 */
export const openCustomerPortal = async (
  gameDataOrOptions?: GameData | CustomerPortalOptions,
  target: string = "_self"
): Promise<void> => {
  // Handle both old signature (gameData, target) and new signature (options)
  let options: CustomerPortalOptions;
  
  if (gameDataOrOptions && (typeof gameDataOrOptions === 'object') && 
      ('gameData' in gameDataOrOptions || 'target' in gameDataOrOptions || 'onLoadingStart' in gameDataOrOptions)) {
    // New options-based signature
    options = gameDataOrOptions as CustomerPortalOptions;
  } else {
    // Legacy signature for backward compatibility
    options = {
      gameData: gameDataOrOptions as GameData,
      target,
    };
  }

  const { gameData, target: finalTarget = "_self", onLoadingStart } = options;

  try {
    // Start loading state
    onLoadingStart?.();
    
    // Use the new authenticated API endpoint
    const { url } = await getCustomerPortalUrl();
    window.open(url, finalTarget, "noopener,noreferrer");
    
    // Don't call onLoadingEnd here - let the loading state persist until redirect
  } catch (error) {
    console.error("Failed to get customer portal URL:", error);
    
    // Fallback to the old static link if available
    const fallbackUrl = gameData?.stripe_link || gameData?.customer_portal_url || "/";
    window.open(fallbackUrl, finalTarget, "noopener,noreferrer");
    
    // Don't call onLoadingEnd here either - let the loading state persist until redirect
  }
};
